import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { finalize } from 'rxjs';
import _ from 'lodash';
import { EmployeeAccount } from '../../../models/employee-account/employee-account';
import { EmployeeAccountFormModel, employeeAccountFieldLabels } from '../../../models/employee-account/employee-account-form';
import { PostingModule, SubledgerType } from '../../../models/financial-account/financial-account';
import { BusinessRoles } from '../../../models/roles/roles-group';
import { EmployeeAccountProxyService } from '../../../proxies/employee-account.proxy.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { reportInvalidFormFields } from '../../../utilities/form-error-reporter.util';
import { ControlsOf } from '../../../utilities/form.types';

const LIST_ROUTE = '/employees';

/**
 * One employee enabled for trips and advances (#5775). This page sets the two accounts the employee's lines post
 * to (advances and payable, both on the Employee subledger, both authorizing Petty Cash) and whether it can take
 * new trips. Created and enabled from the list's "Add employee" modal, never here.
 */
@Component({
  selector: 'app-employee-details',
  templateUrl: './employee-details.component.html',
})
export class EmployeeDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly proxy = inject(EmployeeAccountProxyService);
  private readonly userAuth = inject(UserAuthenticationService);

  readonly canEdit = this.userAuth.roles.includes(BusinessRoles.BusinessAdmin);
  readonly fieldLabels = employeeAccountFieldLabels;
  readonly subledgerEmployee = SubledgerType.Employee;
  readonly pettyCash = PostingModule.PettyCash;

  readonly isLoading = signal(0);
  /** Always false: an employee is enabled from the list's modal, so this page only edits. */
  readonly isNew = signal(false);
  readonly currentId = signal<string | null>(null);
  readonly headerTitle = signal(this.label('EmployeeAccount_Details_Title', 'Employee'));
  readonly account = signal<EmployeeAccount | null>(null);

  readonly menuItems = computed<MenuItem[]>(() => [
    { label: this.label('EmployeeAccount_Title', 'Employees'), route: LIST_ROUTE },
    { label: this.headerTitle() },
  ]);

  readonly saveMenuItems: MenuItem[] = [
    {
      label: this.label('Save and Close', 'Save and Close'),
      icon: 'pi pi-save',
      command: () => this.save(true),
    },
  ];

  readonly form = new FormGroup<ControlsOf<EmployeeAccountFormModel>>({
    advanceGlAccountReference: new FormControl<string | null>(null, Validators.required),
    apGlAccountReference: new FormControl<string | null>(null, Validators.required),
    isActive: new FormControl(true, { nonNullable: true }),
  });

  /** Display-only companions the account picker fills in beside each reference. */
  readonly advanceNumberControl = new FormControl<string | null>(null);
  readonly advanceNameControl = new FormControl<string | null>(null);
  readonly apNumberControl = new FormControl<string | null>(null);
  readonly apNameControl = new FormControl<string | null>(null);

  ngOnInit(): void {
    const id = this.route.snapshot.queryParamMap.get('id');
    if (_.isNil(id) || id === 'new') {
      this.back();
      return;
    }
    this.currentId.set(id);
    this.loadItem(id);
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private loadItem(id: string): void {
    this.startLoading();
    this.proxy
      .getById(id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        const account = response.data;
        if (!_.isNil(account)) this.setAccount(account);
      });
  }

  private setAccount(account: EmployeeAccount): void {
    this.account.set(account);
    this.headerTitle.set(account.name);
    this.form.patchValue(
      {
        advanceGlAccountReference: account.accounting.advanceGlAccount.reference,
        apGlAccountReference: account.accounting.apGlAccount.reference,
        isActive: account.accounting.isActive,
      },
      { emitEvent: false },
    );
    this.advanceNumberControl.setValue(account.accounting.advanceGlAccount.number, { emitEvent: false });
    this.apNumberControl.setValue(account.accounting.apGlAccount.number, { emitEvent: false });
    if (!this.canEdit) this.form.disable({ emitEvent: false });
    this.form.markAsPristine();
  }

  save(shouldClose = false): void {
    const id = this.currentId();
    if (_.isNil(id)) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return;
    }

    const value = this.form.getRawValue();
    const advance = value.advanceGlAccountReference;
    const ap = value.apGlAccountReference;
    if (_.isNil(advance) || _.isNil(ap)) return;

    this.startLoading();
    this.proxy
      .update(id, { advanceGlAccountReference: advance, apGlAccountReference: ap, isActive: value.isActive })
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        const saved = response.data;
        if (_.isNil(saved)) return;
        GlobalMessageUtil.success(this.label('EmployeeAccount_Title', 'Employees'), this.label('EmployeeAccount_Saved', 'Employee saved.'));
        if (shouldClose) {
          this.back();
          return;
        }
        this.setAccount(saved);
      });
  }

  back(): void {
    this.router.navigate([LIST_ROUTE]);
  }

  private label(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return value === key ? fallback : value;
  }
}
